import { useEffect, useState } from "react";
import { blobRequest } from "../api/index.js";

// Profile photos are PHI. They are only ever fetched from the authenticated API and held in memory as
// object URLs. Never write the photo or its object URL to localStorage / sessionStorage.

const PROFILE_PHOTO_PATH = "/user/profilepic/";

// Full imageUrl (including ?v=) -> Promise<object URL | null>
const cache = new Map();
let cacheToken = null;

const currentToken = () => sessionStorage.getItem("token") || localStorage.getItem("token");

const revoke = (promise) => promise.then((objectUrl) => objectUrl && URL.revokeObjectURL(objectUrl));

const photoPath = (imageUrl) => imageUrl.split("?")[0];

export const isProfilePhotoPath = (imageUrl) =>
  typeof imageUrl === "string" && imageUrl.startsWith(PROFILE_PHOTO_PATH);

export const clearProfilePhotoCache = () => {
  cache.forEach(revoke);
  cache.clear();
};

const loadProfilePhoto = (imageUrl) => {
  // A different session (logout, login as someone else) must never see the previous session's photos.
  const token = currentToken();
  if (token !== cacheToken) {
    clearProfilePhotoCache();
    cacheToken = token;
  }

  if (!cache.has(imageUrl)) {
    // A new ?v= replaces the previous version of the same user's photo.
    [...cache.keys()]
      .filter((key) => photoPath(key) === photoPath(imageUrl))
      .forEach((key) => {
        revoke(cache.get(key));
        cache.delete(key);
      });

    cache.set(
      imageUrl,
      blobRequest(imageUrl, "user")
        .then((res) => URL.createObjectURL(res.data))
        .catch((err) => {
          // 404 means no photo or not allowed to see it: remember that. Anything else may be transient.
          if (err?.response?.status !== 404) cache.delete(imageUrl);
          return null;
        })
    );
  }

  return cache.get(imageUrl);
};

/**
 * Returns an object URL for a user's profile photo, or null to show the generic avatar.
 * Only API paths (/user/profilepic/<userId>?v=...) are fetched. Anything else, including "" and old
 * public S3 URLs left in a session from before the switch, is treated as "no photo".
 */
export const useProfilePhoto = (imageUrl) => {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    setSrc(null);

    if (!isProfilePhotoPath(imageUrl)) return undefined;

    let active = true;
    loadProfilePhoto(imageUrl).then((objectUrl) => {
      if (active) setSrc(objectUrl);
    });

    return () => {
      active = false;
    };
  }, [imageUrl]);

  return src;
};
